import React, { FC, useEffect, useState } from "react";
import {
  Table,
  Descriptions,
  Tag,
  Empty,
  Collapse,
  Popover,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";

import ProjectPageShell, {
  useProjectContext,
} from "../shared/ProjectPageShell";
import { GenericStatusPill } from "../shared/statusColors";
import purchaseRequestApis from "../../../api/master/purchaseRequest";

const fmtDate = (d?: string) => (d ? dayjs(d).format("DD-MMM-YYYY") : "-");

const fmtAddress = (val: any): string => {
  if (val == null) return "-";
  if (typeof val === "string") return val;
  if (typeof val === "object") {
    const parts = [
      val.deliveryLocationName,
      val.addressLine1,
      val.addressLine2,
      val.cityName,
      val.districtName,
      val.stateName,
      val.countryName,
      val.pincode,
    ].filter((p) => p && String(p).trim().length > 0);
    return parts.length ? parts.join(", ") : "-";
  }
  return String(val);
};

const AddressPopover = ({ value }: { value: any }) => {
  if (!value || typeof value !== "object") {
    return <span>{value || "-"}</span>;
  }
  const short = value?.deliveryLocationName || value?.addressLine1 || "-";
  return (
    <Popover
      title="Address"
      content={
        <div style={{ maxWidth: 320, fontSize: 12 }}>
          {value.addressLine1 ? <div>{value.addressLine1}</div> : null}
          {value.addressLine2 ? <div>{value.addressLine2}</div> : null}
          <div>
            {[value.cityName, value.districtName, value.stateName, value.countryName]
              .filter(Boolean)
              .join(", ")}
          </div>
          {value.pincode ? <div>PIN: {value.pincode}</div> : null}
          {value.contactPersonName ? (
            <div style={{ marginTop: 6 }}>
              <strong>Contact:</strong> {value.contactPersonName}
              {value.mobileNumber ? ` (${value.mobileNumber})` : ""}
            </div>
          ) : null}
        </div>
      }
    >
      <span style={{ color: "#2557d6", cursor: "pointer", textDecoration: "underline" }}>
        {short}
      </span>
    </Popover>
  );
};

const ViewPurchaseRequest: FC = () => {
  const { prId } = useParams<{ prId: string }>();
  const navigate = useNavigate();
  const { projectName } = useProjectContext();

  const [loading, setLoading] = useState(true);
  const [header, setHeader] = useState<any>({});
  const [lines, setLines] = useState<any[]>([]);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [approvals, setApprovals] = useState<any[]>([]);

  useEffect(() => {
    if (prId) fetchPR();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prId]);

  const fetchPR = async () => {
    try {
      setLoading(true);
      const res: any = await purchaseRequestApis.getById(prId);
      const { response, data, status, message } = res || {};
      if (status) {
        const payload =
          (response?.prHeader ? response : null) ||
          (data?.prHeader ? data : null) ||
          response?.data ||
          data?.data ||
          response ||
          data ||
          {};
        setHeader(payload?.prHeader || {});
        setLines(payload?.prLines || []);
        setAttachments(payload?.prHeader?.dms || payload?.dms || []);
        setApprovals(payload?.prApprovals || []);
      } else if (message) {
        Swal.fire("Error", message, "error");
      }
    } catch (err) {
      console.error("Failed to fetch PR:", err);
    } finally {
      setLoading(false);
    }
  };

  const hierarchyVis = {
    tower: lines.some(
      (l: any) =>
        l?.towerBlockVillaName ||
        l?.towerBlockName ||
        l?.siteStructureUnitName
    ),
    floor: lines.some((l: any) => l?.floorNo),
    flat: lines.some(
      (l: any) =>
        l?.siteStructureSubUnitName || l?.flatLabel || l?.flatId
    ),
    location: lines.some((l: any) => l?.locationName),
  };

  const totalQty = lines.reduce(
    (sum: number, l: any) => sum + (Number(l?.quantity) || 0),
    0
  );

  const lineColumns: any[] = [
    {
      title: "#",
      key: "lineNo",
      width: 50,
      render: (_: any, _r: any, idx: number) => idx + 1,
    },
    {
      title: "Material Category",
      dataIndex: "categoryName",
      key: "categoryName",
      width: 160,
    },
    {
      title: "Material Name",
      dataIndex: "materialName",
      key: "materialName",
      width: 200,
    },
    {
      title: "Make",
      dataIndex: "makeInfo",
      key: "makeInfo",
      width: 140,
      render: (v: any, row: any) => {
        if (Array.isArray(v) && v.length > 0) {
          return v.map((m: any) => m.makeName).filter(Boolean).join(", ") || "-";
        }
        return row?.makeLabel || "-";
      },
    },
    {
      title: "Specification",
      dataIndex: "specification",
      key: "specification",
      width: 180,
    },
    {
      title: "Req Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 130,
      align: "right" as const,
      render: (v: any, row: any) => {
        const qty = v == null ? "-" : String(Number(v));
        return `${qty}${row?.uomName ? ` ${row.uomName}` : ""}`;
      },
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      key: "remarks",
      width: 160,
      render: (v: any) => v || "-",
    },
  ];

  if (hierarchyVis.tower) {
    lineColumns.push({
      title: "Tower / Block / Villa",
      dataIndex: "towerBlockVillaName",
      key: "towerBlockVillaName",
      width: 160,
      render: (v: any, row: any) =>
        v || row?.towerBlockName || row?.siteStructureUnitName || "-",
    });
  }
  if (hierarchyVis.floor) {
    lineColumns.push({
      title: "Floor",
      dataIndex: "floorNo",
      key: "floorNo",
      width: 90,
      render: (v: any) => (v != null ? String(v) : "-"),
    });
  }
  if (hierarchyVis.flat) {
    lineColumns.push({
      title: "Flat",
      dataIndex: "siteStructureSubUnitName",
      key: "flat",
      width: 140,
      render: (v: any, row: any) =>
        v || row?.flatLabel || row?.flatName || "-",
    });
  }
  if (hierarchyVis.location) {
    lineColumns.push({
      title: "Location",
      dataIndex: "locationName",
      key: "locationName",
      width: 140,
      render: (v: any) => v || "-",
    });
  }
  lineColumns.push({
    title: "Status",
    dataIndex: "actualStatus",
    key: "actualStatus",
    width: 130,
    render: (v: any, row: any) => (
      <GenericStatusPill status={v || row?.status || "-"} />
    ),
  });

  const headerStatus = header?.actualStatus || header?.status;

  const prLabel = `PR ID - ${header?.prSequence || prId || "-"}`;

  return (
    <ProjectPageShell
      headTitle="View Purchase Request"
      breadcrumb={[
        { label: projectName },
        { label: "Purchase Request" },
        { label: header?.prSequence || prId || "-" },
      ]}
      title={prLabel}
      eyebrow="Purchase Request"
      onBack={() => navigate(-1)}
      actions={
        <>
          {headerStatus ? <GenericStatusPill status={headerStatus} /> : null}
          {header?.isStrictApproval ? (
            <Tag color="purple" style={{ marginLeft: 8 }}>
              Strict
            </Tag>
          ) : null}
        </>
      }
      loading={loading}
    >
      <Collapse
                defaultActiveKey={["1", "2", "3"]}
                items={[
                  {
                    key: "1",
                    label: "General Information",
                    children: (
                      <Descriptions
                        bordered
                        size="small"
                        column={{ xs: 1, sm: 2, md: 3 }}
                      >
                        <Descriptions.Item label="Site">
                          {header?.siteName || "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Site Hierarchy">
                          {header?.segmentLevel || "Site"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Reference Indent Number">
                          {header?.referenceIndentNumber || "NA"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Indent Date">
                          {fmtDate(header?.indentDate)}
                        </Descriptions.Item>
                        <Descriptions.Item label="Contractor">
                          {header?.contractorName || "NA"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Requested By">
                          {header?.requestByName || "-"}
                        </Descriptions.Item>

                        <Descriptions.Item label="Need By Date">
                          {fmtDate(
                            header?.needByDate || header?.lastDateOfSubmission
                          )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Approvals">
                          {approvals.length > 0
                            ? `${approvals.length} approver(s)`
                            : "-"}
                        </Descriptions.Item>
                        <Descriptions.Item label="Delivery Address">
                          <AddressPopover value={header?.deliveryAddressName} />
                        </Descriptions.Item>
                      </Descriptions>
                    ),
                  },
                  {
                    key: "2",
                    label: `Material Information (${lines.length})`,
                    children: (
                      <Table
                        className="customTable pms-hub-table"
                        columns={lineColumns}
                        dataSource={lines}
                        rowKey={(r: any, i?: number) =>
                          String(r?.prLineId ?? r?.lineNo ?? i ?? Math.random())
                        }
                        pagination={false}
                        size="small"
                        scroll={{ x: 1300 }}
                        summary={() => (
                          <Table.Summary.Row>
                            <Table.Summary.Cell index={0} colSpan={5}>
                              <strong>Total Qty</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                              index={5}
                              align="right"
                            >
                              <strong>{totalQty}</strong>
                            </Table.Summary.Cell>
                            <Table.Summary.Cell
                              index={6}
                              colSpan={lineColumns.length - 6}
                            >
                              {""}
                            </Table.Summary.Cell>
                          </Table.Summary.Row>
                        )}
                        locale={{ emptyText: <Empty description="No items" /> }}
                      />
                    ),
                  },
                  {
                    key: "3",
                    label: "Other Information",
                    children: (
                      <>
                        {approvals.length > 0 && (
                          <>
                            <h6 style={{ fontWeight: 600, margin: "0 0 8px" }}>
                              Approvals ({approvals.length})
                            </h6>
                            <Table
                              className="customTable pms-hub-table"
                              columns={[
                                {
                                  title: "Level",
                                  dataIndex: "level",
                                  key: "level",
                                  width: 100,
                                },
                                {
                                  title: "Approver",
                                  dataIndex: "userName",
                                  key: "userName",
                                },
                                {
                                  title: "Role",
                                  dataIndex: "roleName",
                                  key: "roleName",
                                  width: 160,
                                },
                                {
                                  title: "Date",
                                  dataIndex: "approvalDate",
                                  key: "approvalDate",
                                  width: 140,
                                  render: (d: string) => fmtDate(d),
                                },
                                {
                                  title: "Status",
                                  dataIndex: "status",
                                  key: "status",
                                  width: 130,
                                  render: (s: string) => (
                                    <GenericStatusPill status={s || "-"} />
                                  ),
                                },
                              ]}
                              dataSource={approvals}
                              rowKey={(r: any, i?: number) =>
                                String(r?.userId ?? i ?? Math.random())
                              }
                              pagination={false}
                              size="small"
                            />
                          </>
                        )}

                        {attachments.length > 0 && (
                          <>
                            <h6 style={{ fontWeight: 600, margin: "16px 0 8px" }}>
                              Attachments ({attachments.length})
                            </h6>
                            <ul style={{ paddingLeft: 18 }}>
                              {attachments.map((doc: any, idx: number) => (
                                <li key={doc?.docId ?? idx}>
                                  {doc?.filePath ? (
                                    <a
                                      href={doc.filePath}
                                      target="_blank"
                                      rel="noreferrer"
                                    >
                                      {doc?.fileName ||
                                        doc?.moduleFileType ||
                                        "View"}
                                    </a>
                                  ) : (
                                    <span>{doc?.fileName || "-"}</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                        {approvals.length === 0 && attachments.length === 0 ? (
                          <Empty description="No additional information" />
                        ) : null}
                      </>
                    ),
                  },
                ]}
              />
    </ProjectPageShell>
  );
};

export default ViewPurchaseRequest;
